import React, { useState, useRef, useEffect } from 'react';

const ImageModal = ({ isOpen, onClose, imageUrl, title, altText }) => {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [lastTouchDistance, setLastTouchDistance] = useState(0);
  const imageRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      // Reset zoom and position when modal opens
      setScale(1);
      setPosition({ x: 0, y: 0 });
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // Handle touch start for both single touch (drag) and multi-touch (zoom)
  const handleTouchStart = (e) => {
    e.preventDefault();
    
    if (e.touches.length === 1) {
      // Single touch - start dragging
      setIsDragging(true);
    } else if (e.touches.length === 2) {
      // Multi-touch - start zooming
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.sqrt(
        Math.pow(touch2.clientX - touch1.clientX, 2) + 
        Math.pow(touch2.clientY - touch1.clientY, 2)
      );
      setLastTouchDistance(distance);
      setIsDragging(false);
    }
  };

  // Handle touch move for dragging and zooming
  const handleTouchMove = (e) => {
    e.preventDefault();
    
    if (e.touches.length === 1 && isDragging && scale > 1) {
      // Single touch drag (only when zoomed in)
      const touch = e.touches[0];
      const rect = containerRef.current.getBoundingClientRect();
      const x = touch.clientX - rect.left - rect.width / 2;
      const y = touch.clientY - rect.top - rect.height / 2;
      
      setPosition({ x, y });
    } else if (e.touches.length === 2) {
      // Multi-touch zoom
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.sqrt(
        Math.pow(touch2.clientX - touch1.clientX, 2) + 
        Math.pow(touch2.clientY - touch1.clientY, 2)
      );
      
      if (lastTouchDistance > 0) {
        const scaleChange = distance / lastTouchDistance;
        const newScale = Math.max(0.5, Math.min(4, scale * scaleChange));
        setScale(newScale);
        
        // Reset position if zooming out to 1x or less
        if (newScale <= 1) {
          setPosition({ x: 0, y: 0 });
        }
      }
      
      setLastTouchDistance(distance);
    }
  };

  // Handle touch end
  const handleTouchEnd = (e) => {
    setIsDragging(false);
    setLastTouchDistance(0);
  };

  // Handle mouse wheel for desktop zooming
  const handleWheel = (e) => {
    e.preventDefault();
    const scaleChange = e.deltaY > 0 ? 0.9 : 1.1;
    const newScale = Math.max(0.5, Math.min(4, scale * scaleChange));
    setScale(newScale);
    
    // Reset position if zooming out to 1x or less
    if (newScale <= 1) {
      setPosition({ x: 0, y: 0 });
    }
  };

  // Double tap to zoom
  const handleDoubleClick = () => {
    if (scale === 1) {
      setScale(2);
    } else {
      setScale(1);
      setPosition({ x: 0, y: 0 });
    }
  };

  const resetZoom = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };

  return (
    <div 
      className="image-modal-overlay" 
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
        touchAction: 'none' // Prevent default touch behaviors
      }}
    >
      <div 
        className="image-modal-content"
        onClick={e => e.stopPropagation()}
        style={{
          position: 'relative',
          maxWidth: '95%',
          maxHeight: '95%',
          backgroundColor: '#fff',
          borderRadius: '8px',
          padding: '20px',
          overflow: 'hidden'
        }}
      >
        {/* Close button */}
        <button 
          className="image-modal-close"
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            border: 'none',
            background: 'rgba(0, 0, 0, 0.7)',
            color: 'white',
            fontSize: '24px',
            cursor: 'pointer',
            padding: '5px 10px',
            borderRadius: '50%',
            zIndex: 1001,
            width: '40px',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          ×
        </button>

        {/* Reset zoom button (only show when zoomed) */}
        {scale !== 1 && (
          <button
            onClick={resetZoom}
            style={{
              position: 'absolute',
              top: '10px',
              left: '10px',
              border: 'none',
              background: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              fontSize: '14px',
              cursor: 'pointer',
              padding: '8px 12px',
              borderRadius: '20px',
              zIndex: 1001
            }}
          >
            Reset Zoom
          </button>
        )}

        {/* Title */}
        {title && (
          <h3 style={{
            margin: '0 0 15px 0',
            color: '#333',
            fontSize: '18px',
            textAlign: 'center',
            paddingRight: '50px' // Account for close button
          }}>
            {title}
          </h3>
        )}

        {/* Image container */}
        <div
          ref={containerRef}
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            overflow: 'hidden',
            cursor: scale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'zoom-in',
            height: '70vh',
            touchAction: 'none'
          }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onWheel={handleWheel}
          onDoubleClick={handleDoubleClick}
        >
          <img
            ref={imageRef}
            src={imageUrl}
            alt={altText || title}
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              transform: `scale(${scale}) translate(${position.x}px, ${position.y}px)`,
              transition: isDragging ? 'none' : 'transform 0.2s ease',
              userSelect: 'none',
              pointerEvents: 'none'
            }}
          />
        </div>

        {/* Instructions */}
        <p style={{
          margin: '10px 0 0 0',
          color: '#666',
          fontSize: '12px',
          textAlign: 'center'
        }}>
          📱 Pinch to zoom • Double tap to zoom • Drag to move
        </p>
      </div>
    </div>
  );
};

export default ImageModal; 